import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';

// Generate or get visitor ID from localStorage
const getVisitorId = () => {
  let visitorId = localStorage.getItem('bulkbytes_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 12) + Date.now().toString(36);
    localStorage.setItem('bulkbytes_visitor_id', visitorId);
  }
  return visitorId;
};

// Get session start time
const getSessionStart = () => {
  let sessionStart = sessionStorage.getItem('bulkbytes_session_start');
  if (!sessionStart) {
    sessionStart = new Date().toISOString();
    sessionStorage.setItem('bulkbytes_session_start', sessionStart);
  }
  return sessionStart;
};

// Track page view
export const trackPageView = async (page) => {
  try {
    const visitorId = getVisitorId();
    const sessionStart = getSessionStart();
    
    await axios.post(`${API}/analytics/pageview`, {
      visitor_id: visitorId,
      page: page,
      referrer: document.referrer || null,
      session_start: sessionStart
    });
  } catch (error) {
    // Silent fail for analytics
    console.debug('Analytics pageview error:', error);
  }
};

// Track custom events
export const trackEvent = async (eventType, packageId = null, metadata = {}) => {
  try {
    const visitorId = getVisitorId();
    
    await axios.post(`${API}/analytics/event`, {
      visitor_id: visitorId,
      event_type: eventType,
      package_id: packageId,
      metadata: metadata
    });
  } catch (error) {
    console.debug('Analytics event error:', error);
  }
};

// Update session duration
const updateSessionDuration = async () => {
  try {
    const visitorId = getVisitorId();
    const sessionStart = sessionStorage.getItem('bulkbytes_session_start');
    
    if (sessionStart) {
      const duration = Math.floor((Date.now() - new Date(sessionStart).getTime()) / 1000);
      await axios.put(`${API}/analytics/session/${visitorId}?duration=${duration}`);
    }
  } catch (error) {
    console.debug('Analytics session error:', error);
  }
};

// Analytics Provider component
export function AnalyticsTracker() {
  const location = useLocation();
  const lastPath = useRef('');

  useEffect(() => {
    // Track page view on route change
    if (location.pathname !== lastPath.current) {
      lastPath.current = location.pathname;
      trackPageView(location.pathname);
    }
  }, [location]);

  useEffect(() => {
    // Update session duration every 30 seconds
    const interval = setInterval(updateSessionDuration, 30000);
    
    // Update on page unload
    const handleUnload = () => {
      updateSessionDuration();
    };
    
    window.addEventListener('beforeunload', handleUnload);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  return null; // This component doesn't render anything
}

// Hook for tracking package views
export const usePackageTracking = (packageId) => {
  const tracked = useRef(false);
  
  useEffect(() => {
    if (packageId && !tracked.current) {
      tracked.current = true;
      trackEvent('package_view', packageId);
    }
  }, [packageId]);
};

export { getVisitorId };
